import TrustedHtml from '@components/common/TrustedHtml/TrustedHtml'
import Close from '@components/icons/Close/Close'
import Button from '@components/ui/ButtonV2/Button'
import s from '@components/ui/NewModal/NewModal.module.scss'
import { usecustomerAddressDelete } from '@features/account/api/customer-address-delete'
import {
  BaseAddress,
  CustomerAddressDeleteMutation,
} from '@lib/graphql/graphql'
import { useDisclosure } from '@lib/helpers/useDisclosure'
import { useCustomErrors } from '@lib/hooks/useCustomErrors'
import React, { PropsWithChildren } from 'react'
import { Dialog, Modal, ModalOverlay } from 'react-aria-components'
import { IoCardOutline } from 'react-icons/io5'
import { MdOutlineLocalShipping } from 'react-icons/md'
import dafStyles from './DeleteAddressForm.module.scss'

interface Props {
  address?: BaseAddress
  isDefaultBilling?: boolean
  isDefaultShipping?: boolean
  onDeleteClick?: () => void
}
type NativeAttrs = Omit<React.HTMLAttributes<HTMLDivElement>, keyof Props>

export type DeleteAddressFormProps = Props & NativeAttrs

const DeleteAddressForm = React.forwardRef<
  HTMLDivElement,
  PropsWithChildren<DeleteAddressFormProps>
>(
  (
    {
      className,
      address,
      isDefaultBilling,
      isDefaultShipping,
      onDeleteClick,
      children,
      ...props
    },
    ref,
  ) => {
    const { isOpen, open, close } = useDisclosure()

    const { mutateAsync, isPending } = usecustomerAddressDelete()

    const { customErrors, loading, addError, clearErrors } = useCustomErrors()

    const onDelete = async () => {
      try {
        const response = (await mutateAsync({
          addressId: address.id,
        })) as CustomerAddressDeleteMutation

        if (response.customerAddressDelete.customer?.id) {
          clearErrors()
        }
      } catch (error) {
        console.error(error)

        addError({
          code: 'unknown',
          message: 'Something went wrong',
        })
      }
    }

    const handleOpenDeleteDialog = () => {
      onDeleteClick && onDeleteClick()
      open()
    }

    const addressName = address.metadata?.find((x) => x.key == 'name')?.value

    return (
      <>
        <ModalOverlay
          isDismissable
          isOpen={isOpen}
          onOpenChange={(isOpen) => (isOpen ? open() : close())}
          className={s.overlay}
        >
          <Modal className={s.modal}>
            <Dialog>
              <div className={s.close}>
                <button className={s.closeButton} type="button" onClick={close}>
                  <Close className="h-6 w-6" />
                </button>
              </div>
              <div className={s.root}>
                <div className={s.section}>
                  <div className={s.headline}>DELETE ADDRESS</div>
                  <p>
                    Are you sure you want to delete this address? Please keep in
                    mind that this action cannot be undone.
                  </p>
                </div>
                <div className={s.section}>
                  <p>
                    <div className={dafStyles.address__title}>
                      <span>
                        <b>{addressName !== '' ? addressName : 'Address'}</b>
                        {isDefaultBilling && isDefaultShipping ? (
                          <>
                            <div className={dafStyles.tooltipWrapper}>
                              <IoCardOutline />
                              <div className={dafStyles.tooltip}>
                                <p>Default billing</p>
                              </div>
                            </div>
                            <div className={dafStyles.tooltipWrapper}>
                              <MdOutlineLocalShipping />
                              <div className={dafStyles.tooltip}>
                                <p>Default shipping</p>
                              </div>
                            </div>
                          </>
                        ) : isDefaultBilling ? (
                          <div className={dafStyles.tooltipWrapper}>
                            <IoCardOutline />
                            <div className={dafStyles.tooltip}>
                              <p>Default billing</p>
                            </div>
                          </div>
                        ) : (
                          isDefaultShipping && (
                            <div className={dafStyles.tooltipWrapper}>
                              <MdOutlineLocalShipping />
                              <div className={dafStyles.tooltip}>
                                <p>Default shipping</p>
                              </div>
                            </div>
                          )
                        )}
                      </span>
                    </div>

                    <TrustedHtml
                      html={address.formatted?.replaceAll('\n', '<br />')}
                    />
                  </p>
                </div>
                <div className={s.section}>
                  {customErrors && customErrors.length > 0 && (
                    <div className={s.errorContainer}>
                      {customErrors.map((error) => (
                        <div key={error.code} className={s.error}>
                          {error.message}
                        </div>
                      ))}
                    </div>
                  )}

                  <Button
                    size="small"
                    variant="secondary"
                    onClick={onDelete}
                    loading={isPending || loading}
                  >
                    Delete
                  </Button>
                  <Button as="a" size="small" variant="buy" onClick={close}>
                    Cancel
                  </Button>
                </div>
              </div>
            </Dialog>
          </Modal>
        </ModalOverlay>
        <a onClick={handleOpenDeleteDialog}>Delete</a>
      </>
    )
  },
)

DeleteAddressForm.displayName = 'DeleteAddressForm'
export default DeleteAddressForm
